import type {
	GetFlippedElementPositionsBeforeUpdateArgs,
	ParentBCRs,
	ChildIdsToParentBCRs,
	ChildIdsToParents,
	FlippedElementPositionDatumBeforeUpdate,
	FlippedElementPositionsBeforeUpdateReturnType,
} from "./types";
import type { InProgressAnimations } from "../../../types";
import * as constants from "../../../constants";
import { toArray } from "../../../../../../utils/to";
import { getRects } from "../../utils";

export const cancelInProgressAnimations = (
	inProgressAnimations: InProgressAnimations,
	animatingElements: HTMLElement[],
) => {
	Object.keys(inProgressAnimations).forEach((id) => {
		if (inProgressAnimations[id].destroy) {
			inProgressAnimations[id].destroy!();
		}
		if (inProgressAnimations[id].onAnimationEnd) {
			inProgressAnimations[id].onAnimationEnd!(true);
		}
		delete inProgressAnimations[id];
	});
	animatingElements.forEach((el) => {
		el.style.transform = "";
		el.style.opacity = "";
	});
};

export const getFlippedElementPositionsBeforeUpdate = ({
	element,
	flipCallbacks = {},
	inProgressAnimations = {},
}: GetFlippedElementPositionsBeforeUpdateArgs): FlippedElementPositionsBeforeUpdateReturnType => {
	/** TODO: Check again if we should get elements using querySelectorAll or anything else for performance reasons. */
	/** NOTE: Optimize it again. Why? Because we are using querySelectorAll twice. */
	/** NOTE: Because, we can optimize in case: the elements are out of viewport.*/
	const flippedElements = toArray<HTMLElement>(
		element.querySelectorAll(`[${constants.DATA_FLIP_ID}]`),
	);
	const inverseFlippedElements = toArray<HTMLElement>(
		element.querySelectorAll(`[${constants.DATA_INVERSE_FLIP_ID}]`),
	);

	/** This is for exit animations. So, we can re-insert exiting elements in the DOM later. */
	const childIdsToParents: ChildIdsToParents = {};
	const childIdsToParentBCRs: ChildIdsToParentBCRs = {};
	const parentBCRs: ParentBCRs = [];
	flippedElements
		.filter(
			(el) =>
				el &&
				flipCallbacks &&
				flipCallbacks[el.dataset.flipId!] &&
				flipCallbacks[el.dataset.flipId!].onExit,
		)
		.forEach((el) => {
			let parent = el.parentNode as HTMLElement;
			/** This won't work for IE11?? */
			if (el.closest) {
				const exitContainer = el.closest(
					`[${constants.DATA_EXIT_CONTAINER}]`,
				) as HTMLElement;
				if (exitContainer) {
					parent = exitContainer;
				}
			}

			/** Because we can have multiple elements with the same parent. So, we need to check if the parent is in the array or not. */
			let bcrIndex = parentBCRs.findIndex((n) => n[0] === parent);
			if (bcrIndex === -1) {
				parentBCRs.push([parent, parent.getBoundingClientRect()]);
				bcrIndex = parentBCRs.length - 1;
			}
			childIdsToParentBCRs[el.dataset.flipId!] = parentBCRs[bcrIndex][1];
			childIdsToParents[el.dataset.flipId!] = parent;
		});

	const filteredFlippedElements = getRects(flippedElements);

	const flippedElementPositionsTupleArray: [string, FlippedElementPositionDatumBeforeUpdate][] =
		filteredFlippedElements.map(([child, childBCR]) => {
			const domDataForExitAnimations = {};

			/** Only cache extra data for exit animations if the element has an onExit listener. */
			if (
				flipCallbacks &&
				flipCallbacks[child.dataset.flipId!] &&
				flipCallbacks[child.dataset.flipId!].onExit
			) {
				const parentBCR = childIdsToParentBCRs[child.dataset.flipId!];

				Object.assign(domDataForExitAnimations, {
					element: child,
					parent: childIdsToParents[child.dataset.flipId!],
					childPosition: {
						top: childBCR.top - parentBCR.top,
						left: childBCR.left - parentBCR.left,
						x: childBCR.x - parentBCR.x,
						y: childBCR.y - parentBCR.y,
						right: childBCR.right - parentBCR.right,
						bottom: childBCR.bottom - parentBCR.bottom,
						width: childBCR.width,
						height: childBCR.height,
					},
				});
			}

			return [
				child.dataset.flipId!,
				{
					rect: childBCR,
					//Hmm, why are we using window.getComputedStyle here?
					opacity: parseFloat(window.getComputedStyle(child).opacity || "1"),
					domDataForExitAnimations,
				},
			];
		}) as [string, FlippedElementPositionDatumBeforeUpdate][];

	const flippedElementPositions = flippedElementPositionsTupleArray.reduce<
		Record<string, FlippedElementPositionDatumBeforeUpdate>
	>((map, [flipId, datum]) => {
		map[flipId] = datum;
		return map;
	}, {});

	/** Do this at the very end since we want to cache positions of elements while they are mid-transition. */
	cancelInProgressAnimations(
		inProgressAnimations,
		flippedElements.concat(inverseFlippedElements),
	);

	return {
		flippedElementPositions,
		cachedOrderedFlipIds: filteredFlippedElements.map(([el]) => el.dataset.flipId!),
	};
};
